/* eslint-disable @typescript-eslint/no-unsafe-argument */
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { FeatureFlags, PortfolioAllocationV2, PortfolioAllocationResponse } from '@inyova/models';
import { TranslateService } from '@ngx-translate/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store, Action } from '@ngrx/store';
import { forkJoin, from, of, timer } from 'rxjs';
import { catchError, delay, exhaustMap, filter, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import * as StrategyActions from './strategy.actions';
import * as AppRootActions from '@app/app.actions';
import * as ReportingActions from '@reporting/reporting.actions';
import * as fromAppRoot from '@app/app.reducers';
import * as fromStrategy from './reducers';

import { FinancialSituation, FinancialSituationInvestmentProfile } from '@shared/models/Account';
import {
  Company,
  CompanyListItem,
  CriteriumData,
  CustomerInvestmentData,
  GetStrategy,
  RiskQuestionsSurveyOption,
  RiskSurveyQuestion,
  SearchCompany,
  StrategyStockAllocation,
  TopicData
} from '@shared/models/Strategy';
import { AccountOpeningService } from '@shared/services/account-opening.service';
import { AccountService } from '@shared/services/account.service';
import { ReportingService } from '@shared/services/reporting.service';
import { StrategyService } from '@shared/services/strategy.service';
import { ToastNotificationService } from '@shared/services/toast-notification.service';

@Injectable()
export class StrategyEffects {
  fetchStrategyEditMode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StrategyActions.fetchStrategyEditMode),
      mergeMap(() =>
        this.strategyService.getStrategyEditMode().pipe(
          map((res: { disabled: boolean }) => StrategyActions.fetchStrategyEditModeSuccess({ changeStrategyDisabled: res.disabled })),
          catchError(() => of(StrategyActions.fetchStrategyEditModeFail()))
        )
      )
    )
  );

  updateStrategyEditMode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StrategyActions.updateStrategyEditMode),
      mergeMap(({ editMode }) =>
        this.strategyService.updateStrategyEditMode({ edit_mode: editMode }).pipe(
          map((res: { success: boolean }) => {
            void this.router.navigate(['/strategy']);
            return StrategyActions.updateStrategyEditModeSuccess({ changeStrategyDisabled: res.success });
          }),
          catchError(() => of(StrategyActions.updateStrategyEditModeFail()))
        )
      )
    )
  );

  fetchCustomerAfterEditModeUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StrategyActions.updateStrategyEditModeSuccess),
      map(() => AppRootActions.fetchCustomer())
    )
  );

  discardStrategyChanges$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StrategyActions.discardStrategyChanges),
      mergeMap(() =>
        this.strategyService.discardStrategyChanges().pipe(
          mergeMap(() => [
            StrategyActions.updateStrategyEditMode({ editMode: false }),
            StrategyActions.getLatestStrategyData({ fetchAll: true }),
            StrategyActions.discardStrategyChangesSuccess()
          ]),
          catchError(() => of(StrategyActions.discardStrategyChangesFail()))
        )
      )
    )
  );

  saveStrategyChanges$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StrategyActions.saveStrategyChanges),
      mergeMap(() =>
        this.strategyService.saveStrategyChanges().pipe(
          mergeMap(() => [StrategyActions.updateStrategyEditMode({ editMode: false }), StrategyActions.saveStrategyChangesSuccess()]),
          catchError(() => of(StrategyActions.saveStrategyChangesFail()))
        )
      )
    )
  );

  sendCompanySuggestion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StrategyActions.sendCompanySuggestion),
      mergeMap(({ message }) =>
        this.accountService.createSupportTicket(message).pipe(
          map(() => StrategyActions.sendCompanySuggestionSuccess()),
          catchError(() => of(StrategyActions.sendCompanySuggestionFail()))
        )
      )
    )
  );

  generateStrategy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StrategyActions.generateStrategy),
      concatLatestFrom(() => [this.store.pipe(select(fromAppRoot.selectCurrentInvestAccount))]),
      mergeMap(([, currentAccountDetail]) =>
        this.strategyService.generateStrategy().pipe(
          map(() =>
            StrategyActions.generateStrategySuccess({
              customerAccountDetail: { ...currentAccountDetail, ql_onboarding_step: 4 },
              fetchAll: true
            })
          ),
          catchError(() => of(StrategyActions.generateStrategyFail()))
        )
      )
    )
  );

  getLatestStrategyData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StrategyActions.getLatestStrategyData, StrategyActions.generateStrategySuccess),
      exhaustMap(({ fetchAll }) => {
        const withoutStrategy = [StrategyActions.getTopicsData(), StrategyActions.getCriteriaData(), StrategyActions.getCustomerInvestment()];

        const withStrategy = [
          StrategyActions.getCustomerInvestment(),
          StrategyActions.getRiskQuestions(),
          StrategyActions.getPortfolioCompanyList(),
          StrategyActions.getCompanyList(),
          StrategyActions.fetchStrategyEditMode(),
          StrategyActions.getTopicsData(),
          StrategyActions.getCriteriaData()
        ];

        return !fetchAll ? withoutStrategy : withStrategy;
      })
    )
  );

  getLatestStrategyDataEve2ForExistingCustomers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StrategyActions.getLatestStrategyDataEve2),
      concatLatestFrom(() => [
        this.store.select(fromStrategy.selectIsStrategyInitialised),
        this.store.select(fromAppRoot.selectCustomerAndCurrentInvestAccount),
        this.store.select(fromAppRoot.selectAccountFeatureFlag(FeatureFlags.EVE2))
      ]),
      filter(
        ([, isStrategyInitialised, { currentAccountDetail }, hasFeatureFlag]) =>
          hasFeatureFlag && !isStrategyInitialised && currentAccountDetail.step_status >= 5
      ),
      exhaustMap(() => {
        return [
          StrategyActions.getRiskQuestions(),
          StrategyActions.getFundBreakdown(),
          StrategyActions.getInvestmentDetails(),
          StrategyActions.fetchStockAllocationEve2(),
          StrategyActions.getStrategyInfo()
        ];
      })
    )
  );

  getLatestStrategyDataEve2ForNewCustomers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StrategyActions.getLatestStrategyDataEve2),
      concatLatestFrom(() => [
        this.store.select(fromStrategy.selectIsStrategyInitialised),
        this.store.select(fromAppRoot.selectCustomerAndCurrentInvestAccount),
        this.store.select(fromAppRoot.selectAccountFeatureFlag(FeatureFlags.EVE2))
      ]),
      filter(
        ([, isStrategyInitialised, { currentAccountDetail }, hasFeatureFlag]) =>
          hasFeatureFlag && !isStrategyInitialised && currentAccountDetail.step_status < 5
      ),
      exhaustMap(([, , { currentAccountDetail }]) => {
        if (currentAccountDetail.step_status === 1 && currentAccountDetail.ql_onboarding_step >= 3) {
          return [StrategyActions.getInvestmentPlan()];
        }
        if (currentAccountDetail.step_status >= 2 || currentAccountDetail.ql_onboarding_step >= 4) {
          return [StrategyActions.getInvestmentPlan(), StrategyActions.getFundBreakdown(), StrategyActions.fetchStockAllocationEve2()];
        }
      })
    )
  );

  getTopicsData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StrategyActions.getTopicsData),
      mergeMap(() =>
        this.strategyService.getTopics().pipe(
          map((response: { customer: { topics: TopicData[] }; topics: TopicData[] }) =>
            StrategyActions.getTopicsDataSuccess({ customer: response.customer.topics, topics: response.topics })
          ),
          catchError(() => of(StrategyActions.getTopicsDataFail()))
        )
      )
    )
  );

  getCriteriaData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StrategyActions.getCriteriaData),
      mergeMap(() =>
        this.strategyService.getCriteria().pipe(
          map((response: { customer: { criteria: CriteriumData[] }; criteria: CriteriumData[] }) =>
            StrategyActions.getCriteriaDataSuccess({ customer: response.customer.criteria, criteria: response.criteria })
          ),
          catchError(() => of(StrategyActions.getCriteriaDataFail()))
        )
      )
    )
  );

  getCompanyDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StrategyActions.getCompanyDetail),
      mergeMap(({ companyId }) =>
        this.strategyService.getCompanyDetails(companyId).pipe(
          map((response: { company: Company }) => {
            return StrategyActions.getCompanyDetailSuccess({ company: response.company, loading: false });
          }),
          catchError(() => of(StrategyActions.getCompanyDetailFail()))
        )
      )
    )
  );

  getCompanyList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StrategyActions.getCompanyList),
      mergeMap(() =>
        this.strategyService.getMatches().pipe(
          map((response: { blacklist: CompanyListItem[]; wishlist: CompanyListItem[]; yova_stocks: CompanyListItem[] }) => {
            return StrategyActions.getCompanyListSuccess(response);
          }),
          catchError(() => of(StrategyActions.getCompanyListFail()))
        )
      )
    )
  );

  getPortfolioCompanyList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StrategyActions.getPortfolioCompanyList),
      mergeMap(() =>
        this.strategyService.getStrategy().pipe(
          map((response: GetStrategy) => StrategyActions.getPortfolioCompanyListSuccess(response)),
          catchError(() => of(StrategyActions.getPortfolioCompanyListFail()))
        )
      )
    )
  );

  getStrategySuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StrategyActions.getPortfolioCompanyListSuccess),
      mergeMap(() => {
        return [StrategyActions.fetchStockAllocations(), StrategyActions.getCustomerDiscardedWishes()];
      })
    )
  );

  skipPersonalisationStepAfterGettingCompanyList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StrategyActions.getPortfolioCompanyListSuccess),
      concatLatestFrom(() => this.store.select(fromAppRoot.selectCustomerAndCurrentInvestAccount)),
      filter(
        ([, { customer, currentAccountDetail }]) =>
          customer.hide_personalisation && currentAccountDetail.step_status === 1 && currentAccountDetail.ql_onboarding_step === 4
      ),
      map(() => StrategyActions.updateToAlmostProspect())
    )
  );

  updatePortfolioCompanyList$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(StrategyActions.updatePortfolioCompanyList),
      mergeMap(({ wishedCompanyIDs, blockedCompanyIDs }) => {
        // clear duplicated ids before send ids to backend
        const uniqueWished = [...new Set(wishedCompanyIDs)];
        const uniqueBlocked = [...new Set(blockedCompanyIDs)];

        return this.strategyService
          .updateStrategy({
            company_whitelist: uniqueWished,
            company_blacklist: uniqueBlocked
          })
          .pipe(
            map((response: { success: boolean }) => {
              // TODO: All effects should return an action
              if (response.success) {
                return StrategyActions.getPortfolioCompanyList();
              }
            }),
            catchError(() => of(StrategyActions.updatePortfolioCompanyListFail()))
          );
      })
    );
  });

  getCustomerInvestment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(StrategyActions.getCustomerInvestment, AppRootActions.updateSpDepositSuccess),
      mergeMap(() =>
        this.strategyService.getCustomerInvestment().pipe(
          map((response: { customer: CustomerInvestmentData }) => {
            return StrategyActions.getCustomerInvestmentSuccess({ investmentData: response.customer });
          }),
          catchError(() => of(StrategyActions.getCustomerInvestmentFail()))
        )
      )
    );
  });

  undoStrategyActions$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(StrategyActions.undoStrategyActions),
      mergeMap(() =>
        this.strategyService.undoStrategyActions().pipe(
          mergeMap((response: { success: boolean }) => {
            if (response.success) {
              return [
                StrategyActions.setNotification({ notification: { company: '', type: '', show: false } }),
                StrategyActions.getPortfolioCompanyList()
              ];
            }
            return [];
          }),
          catchError(() => of(StrategyActions.undoStrategyActionsFail()))
        )
      )
    );
  });

  getCustomerDiscardedWishes$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(StrategyActions.getCustomerDiscardedWishes),
      mergeMap(() =>
        this.strategyService.getCustomerDiscardedWishes().pipe(
          map((response: string[]) => {
            return StrategyActions.getCustomerDiscardedWishesSuccess({ discardedWishes: response });
          }),
          catchError(() => of(StrategyActions.getCustomerDiscardedWishesFail()))
        )
      )
    );
  });

  getRiskQuestions$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(StrategyActions.getRiskQuestions),
      concatLatestFrom(() => [this.store.pipe(select(fromAppRoot.selectCustomerAndCurrentInvestAccount))]),
      mergeMap(([, { currentAccountDetail }]) =>
        this.strategyService.getRiskQuestions().pipe(
          map((response: { survey_questions: RiskSurveyQuestion[] }) => {
            // TODO: Move this into specific selector
            response.survey_questions.forEach((question: RiskSurveyQuestion): void => {
              question.survey_answer_options.forEach((option: RiskQuestionsSurveyOption): void => {
                if (question.too_long_options === true) {
                  return;
                }
                // eslint-disable-next-line no-param-reassign
                question.too_long_options = option.option_text.length >= 20;
              });
            });

            return currentAccountDetail.kind === '3a'
              ? StrategyActions.set3ARiskQuestions({ riskSurveyQuestions: response.survey_questions })
              : StrategyActions.setCHRiskQuestions({ riskSurveyQuestions: response.survey_questions });
          }),
          catchError(() => of(StrategyActions.getRiskQuestionsFail()))
        )
      )
    );
  });

  getRiskStockSplit$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(StrategyActions.getRiskStockSplit),
      mergeMap(({ riskAnswers }) =>
        this.strategyService.getRiskStockSplit({ body: riskAnswers }).pipe(
          map((response: { stock_split: number; max_stock_split: number }) => {
            return StrategyActions.getRiskStockSplitSuccess({ riskStockSplit: response.stock_split, maxStockSplit: response.max_stock_split });
          }),
          catchError(() => of(StrategyActions.getRiskStockSplitFail()))
        )
      )
    );
  });

  update$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StrategyActions.updateTopics, StrategyActions.updateInvestment, StrategyActions.updateRisk),
      mergeMap((payload) =>
        this.strategyService.updateStrategy(payload.updateObject).pipe(
          map((response: { success: boolean }) => {
            if (!response.success) {
              return StrategyActions.updateFailed();
            }

            if (payload.type === StrategyActions.updateTopics.type) {
              return StrategyActions.updateTopicsSuccess({ continueToPortfolio: payload.continueToPortfolio, nextStep: 2 });
            }

            if (payload.type === StrategyActions.updateInvestment.type) {
              return StrategyActions.updateInvestmentSuccess({ continueToPortfolio: payload.continueToPortfolio, nextStep: 3 });
            }

            return StrategyActions.updateRiskSuccess({ continueToPortfolio: payload.continueToPortfolio, nextStep: 4 });
          }),
          catchError(() => of(StrategyActions.updateFailed()))
        )
      )
    )
  );

  updateRiskEve2$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StrategyActions.updateRiskEve2),
      concatLatestFrom(() => this.store.pipe(select(fromStrategy.selectStrategyWasChanged))),
      tap(([, { strategyWasChanged }]) => {
        if (strategyWasChanged) {
          this.store.dispatch(StrategyActions.showRiskToStrategyLoaderEve2());
        } else {
          this.store.dispatch(StrategyActions.setTabIndex({ tabIndex: 4 }));
        }
      }),
      filter(([, { strategyWasChanged }]) => strategyWasChanged),
      mergeMap(([{ risk_answers, selected_stock_split }]) => {
        const timestampOne = Date.now();

        const updateRiskCall$ = this.strategyService.updateRisk({ risk_answers, selected_stock_split }).pipe(
          map(() => {
            this.store.dispatch(StrategyActions.updateRiskSuccessEve2());

            const timestampTwo = Date.now();
            // show loader for at least 3.5 seconds
            return Math.max(3500 - (timestampTwo - timestampOne), 0);
          }),
          catchError(() => {
            this.store.dispatch(StrategyActions.updateFailed());
            return of(0);
          })
        );

        return updateRiskCall$.pipe(
          mergeMap((remainingLoadingTime) => timer(remainingLoadingTime).pipe(map(() => StrategyActions.hideRiskToStrategyLoaderEve2())))
        );
      })
    )
  );

  updateRiskRecheckEve2$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StrategyActions.updateRiskRecheckEve2),
      mergeMap(({ risk_answers, selected_stock_split }) =>
        this.strategyService.updateRisk({ risk_answers, selected_stock_split }).pipe(
          tap(() => this.router.navigate(['/'])),
          map(() => StrategyActions.updateRiskRecheckSuccessEve2()),
          catchError(() => of(StrategyActions.updateRiskRecheckFailedEve2()))
        )
      )
    )
  );

  discardRiskRecheckEve2$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(StrategyActions.discardRiskRecheckEve2),
        tap(() => this.router.navigate(['/']))
      ),
    { dispatch: false }
  );

  updateRiskSuccessEve2$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StrategyActions.updateRiskSuccessEve2),
      mergeMap(() => [
        StrategyActions.setTabIndex({ tabIndex: 4 }),
        StrategyActions.getRiskQuestions(),
        StrategyActions.getInvestmentPlan(),
        StrategyActions.getFundBreakdown(),
        StrategyActions.fetchStockAllocationEve2()
      ])
    )
  );

  navigateFromRiskPageToStrategyReview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StrategyActions.navigateFromRiskPageToStrategyReview),
      concatLatestFrom(() => this.store.pipe(select(fromStrategy.selectStrategyWasChanged))),
      mergeMap(([formData, { strategyWasChanged }]) => {
        const actions: Action<string>[] = [];
        if (strategyWasChanged) {
          actions.push(StrategyActions.saveRiskFormDataEve2(formData));
        }
        actions.push(StrategyActions.setTabIndex({ tabIndex: 4 }));

        return from(actions);
      })
    )
  );

  showRiskToStrategyLoader$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StrategyActions.saveRiskFormDataEve2),
      map(() => StrategyActions.showRiskToStrategyLoaderEve2())
    )
  );

  hideRiskToStrategyLoader$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StrategyActions.saveRiskFormDataEve2),
      delay(4000),
      map(() => StrategyActions.hideRiskToStrategyLoaderEve2())
    )
  );

  enterStrategyEditModeEve2$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StrategyActions.enterStrategyEditModeEve2),
      tap(() => this.router.navigate(['/strategy'])),
      map(() => StrategyActions.setTabIndex({ tabIndex: 3 }))
    )
  );

  saveStrategyChangesEve2$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StrategyActions.saveStrategyChangesEve2),
      concatLatestFrom(() => [this.store.select(fromStrategy.selectRiskSurveyFormData), this.store.select(fromStrategy.selectStrategyWasChanged)]),
      mergeMap(([, riskFormData, { strategyWasChanged }]) =>
        strategyWasChanged
          ? this.strategyService.updateRisk(riskFormData).pipe(
              map(() => StrategyActions.saveStrategyChangesSuccessEve2()),
              catchError(() => of(StrategyActions.saveStrategyChangesFailEve2()))
            )
          : of(StrategyActions.discardStrategyChangesEve2())
      )
    )
  );

  saveStrategyChangesSuccessEve2$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StrategyActions.saveStrategyChangesSuccessEve2),
      mergeMap(() => [
        StrategyActions.setTabIndex({ tabIndex: 4 }),
        StrategyActions.getRiskQuestions(),
        StrategyActions.getStrategyInfo(),
        StrategyActions.getInvestmentDetails()
      ])
    )
  );

  updateInvestmentPlan$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StrategyActions.updateInvestmentPlan),
      mergeMap((investmentPlan) =>
        this.strategyService.updateInvestmentPlan(investmentPlan).pipe(
          map(({ investment_plan: investmentPlan }) => StrategyActions.updateInvestmentPlanSuccess({ nextStep: 3, investmentPlan })),
          catchError(() => of(StrategyActions.updateFailed()))
        )
      )
    )
  );

  updateInvestmentPlanSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StrategyActions.updateInvestmentPlanSuccess),
      mergeMap(({ nextStep }) => [StrategyActions.setTabIndex({ tabIndex: nextStep }), StrategyActions.getCustomerInvestment()])
    )
  );

  getInvestmentPlan$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StrategyActions.getInvestmentPlan),
      mergeMap(() =>
        this.strategyService.getInvestmentPlan().pipe(
          map(({ investment_plan: investmentPlan }) => StrategyActions.getInvestmentPlanSuccess(investmentPlan)),
          catchError(() => of(StrategyActions.getInvestmentPlanFail()))
        )
      )
    )
  );

  getStrategyInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StrategyActions.getStrategyInfo),
      mergeMap(() =>
        this.strategyService.getStrategyInfo().pipe(
          map(({ strategy_info: strategyInfo }) => StrategyActions.getStrategyInfoSuccess(strategyInfo)),
          catchError(() => of(StrategyActions.getStrategyInfoFail()))
        )
      )
    )
  );

  getFundBreakdown$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StrategyActions.getFundBreakdown),
      concatLatestFrom(() => [this.store.pipe(select(fromStrategy.selectPortfolioCompanyLength))]),
      filter(([, length]) => length === 0),
      mergeMap(() =>
        this.strategyService.getFundBreakdown().pipe(
          map((breakdown) => StrategyActions.getFundBreakdownSuccess(breakdown)),
          catchError(() => of(StrategyActions.getFundBreakdownFail()))
        )
      )
    )
  );

  getInvestmentDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StrategyActions.getInvestmentDetails),
      mergeMap(() =>
        this.strategyService.getInvestmentDetails().pipe(
          map((strategy) => StrategyActions.getInvestmentDetailsSuccess(strategy)),
          catchError(() => of(StrategyActions.getInvestmentDetailsFail()))
        )
      )
    )
  );

  updateTopicsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StrategyActions.updateTopicsSuccess),
      concatLatestFrom(() => [this.store.pipe(select(fromStrategy.selectStrategyWasChanged))]),
      mergeMap(([{ continueToPortfolio }, { strategyWasChanged }]) => {
        let actions: any[] = [];

        if (continueToPortfolio) {
          actions = [...actions, StrategyActions.getPortfolioCompanyList(), StrategyActions.getCompanyList()];
        }

        if (strategyWasChanged) {
          actions = [...actions, StrategyActions.getTopicsData(), StrategyActions.getCriteriaData()];
        }

        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return actions;
      })
    )
  );

  updateInvestmentSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StrategyActions.updateInvestmentSuccess),
      concatLatestFrom(() => [
        this.store.pipe(select(fromStrategy.selectStrategyWasChanged)),
        this.store.pipe(select(fromAppRoot.selectCurrentAccount))
      ]),
      mergeMap(([{ continueToPortfolio }, { strategyWasChanged }, { id: accountID }]) => {
        if (continueToPortfolio) {
          const actions: Action<string>[] = [
            StrategyActions.getCustomerInvestment(),
            StrategyActions.getPortfolioCompanyList(),
            StrategyActions.getCompanyList()
          ];

          if (strategyWasChanged) {
            actions.push(AppRootActions.fetchCustomerAccountDetail({ accountID }));
          }
          // eslint-disable-next-line @typescript-eslint/no-unsafe-return
          return actions;
        }

        // TODO: Align why do we need to call again
        return of(StrategyActions.getCustomerInvestment());
      })
    )
  );

  updateRiskSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StrategyActions.updateRiskSuccess),
      concatLatestFrom(() => [
        this.store.pipe(select(fromStrategy.selectStrategyWasChanged)),
        this.store.pipe(select(fromAppRoot.selectCurrentAccount))
      ]),
      map(([{ continueToPortfolio }, { redirectUrl }, currentAccount]) => {
        // if user change tab or page URL before finalising strategy generation just call riskQuestion to fill again with updated risk questions
        if (
          currentAccount.step_status === 1 &&
          ((redirectUrl.length && redirectUrl.includes('tabs/')) || (redirectUrl.length && redirectUrl.indexOf('strategy')))
        ) {
          return StrategyActions.getRiskQuestions();
        }
        if (continueToPortfolio && currentAccount.step_status === 1) {
          return StrategyActions.generateStrategy();
        }
        return StrategyActions.getLatestStrategyData({ fetchAll: true });
      })
    )
  );

  updateStrategyAndNavigate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StrategyActions.updateTopicsSuccess, StrategyActions.updateInvestmentSuccess, StrategyActions.updateRiskSuccess),
      concatLatestFrom(() => [
        this.store.pipe(select(fromStrategy.selectStrategyWasChanged)),
        this.store.pipe(select(fromAppRoot.selectCurrentAccount))
      ]),
      map(([{ type, nextStep, continueToPortfolio }, wasChanged, account]) => {
        const tabIndex = account.step_status > 1 ? 5 : account.ql_onboarding_step;
        if (wasChanged.modalOpened && wasChanged.redirectUrl) {
          // tabs/5, tabs/4
          if (wasChanged.redirectUrl.includes('tabs/')) {
            return StrategyActions.setTabIndex({ tabIndex: Number(wasChanged.redirectUrl[wasChanged.redirectUrl.length - 1]) });
          }
          // strategy
          if (wasChanged.redirectUrl.includes('strategy')) {
            return StrategyActions.setTabIndex({ tabIndex });
          }
          // others URL
          return StrategyActions.updateAndNavigateToURL({ url: wasChanged.redirectUrl });
        } else {
          if (type === StrategyActions.updateRiskSuccess.type) {
            if (account.step_status === 1) {
              if (account.ql_onboarding_step < nextStep) {
                return StrategyActions.updateQLOnboardingStep({ step: nextStep });
              }
            }
            return StrategyActions.setTabIndex({ tabIndex });
          } else {
            if (!continueToPortfolio && account.ql_onboarding_step < nextStep) {
              return StrategyActions.updateQLOnboardingStep({ step: nextStep });
            }
            return StrategyActions.setTabIndex({ tabIndex });
          }
        }
      })
    )
  );

  updateAndNavigateFromStrategy$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(StrategyActions.updateAndNavigateToURL),
        tap(({ url }) => {
          void this.router.navigateByUrl(url);
        })
      ),
    { dispatch: false }
  );

  updateRiskRecheck$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StrategyActions.updateRiskRecheck),
      mergeMap(({ risk_answers }) =>
        this.strategyService.updateRiskRecheck({ risk_answers }).pipe(
          tap(() => {
            this.store.dispatch(ReportingActions.getCustomerNotifications());
            void this.router.navigate(['/']);
          }),
          mergeMap((res: { success: boolean }) => {
            if (!res.success) {
              return [StrategyActions.updateFailed()];
            }

            return [StrategyActions.getRiskQuestions(), StrategyActions.getLatestStrategyData({ fetchAll: true })];
          }),
          catchError(() => of(StrategyActions.updateFailed()))
        )
      )
    )
  );

  fetchSearchCompanies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StrategyActions.fetchSearchCompanies),
      mergeMap(({ searchedTerm }) =>
        this.strategyService.searchCompanies(searchedTerm).pipe(
          map((response: { companies: SearchCompany[] }) => {
            return StrategyActions.fetchSearchCompaniesSuccess({ searchCompanies: response.companies });
          }),
          catchError(() => of(StrategyActions.fetchSearchCompaniesFail()))
        )
      )
    )
  );

  fetchStockAllocations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StrategyActions.fetchStockAllocations),
      mergeMap(() =>
        this.strategyService.getStockAllocation().pipe(
          map((response: { data: { id: string; type: string }; meta: StrategyStockAllocation }) => {
            return StrategyActions.fetchStockAllocationsSuccess({ stockAllocation: response.meta });
          }),
          catchError(() => of(StrategyActions.fetchStockAllocationsFail()))
        )
      )
    )
  );

  fetchStockAllocationEve2$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StrategyActions.fetchStockAllocationEve2),
      concatLatestFrom(() => [this.store.pipe(select(fromStrategy.selectStockAllocation))]),
      filter(([, allocation]) => Object.values(allocation).some((weights) => weights.length === 0)),
      mergeMap(() =>
        this.strategyService.getStockAllocationEve2().pipe(
          map(({ stock_allocation: stockAllocation }) => {
            return StrategyActions.fetchStockAllocationEve2Success({ stockAllocation });
          }),
          catchError(() => of(StrategyActions.fetchStockAllocationEve2Fail()))
        )
      )
    )
  );

  getPortfolioAllocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StrategyActions.getWithdrawalData),
      concatLatestFrom(() => [
        this.store.select(fromAppRoot.selectAccountFeatureFlag(FeatureFlags.EVE2)),
        this.store.select(fromStrategy.selectPortfolioAllocation),
        this.store.select(fromStrategy.selectPortfolioAllocationV2),
        this.store.select(fromAppRoot.selectCurrentAccount)
      ]),
      filter(
        ([, hasFeatureFlag, portfolioAllocation, portfolioAllocationV2, account]) =>
          account.kind !== '3a' && (hasFeatureFlag ? portfolioAllocationV2 === null : portfolioAllocation === null)
      ),
      switchMap(([, hasFeatureFlag]) =>
        forkJoin([
          hasFeatureFlag ? this.accountService.getPortfolioAllocationV2() : this.accountService.getPortfolioAllocation(),
          this.reportingService.getEndValues(),
          this.reportingService.getPerformanceGraph()
        ]).pipe(
          map(([allocation, , graphData]) => {
            const accountReportHasData = !!graphData.length;

            const data = hasFeatureFlag ? (allocation as PortfolioAllocationV2) : (allocation as PortfolioAllocationResponse).data.attributes;

            return StrategyActions.getWithdrawalDataSuccess({ data, accountReportHasData, hasFeatureFlag });
          }),
          catchError(() => of(StrategyActions.getWithdrawalDataFail()))
        )
      )
    )
  );

  disableInterview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StrategyActions.disableInterviewNotification),
      withLatestFrom(this.store.select(fromAppRoot.selectCustomer)),
      mergeMap(([action, customer]) => {
        return this.strategyService.disableInterviewNotification(customer.id).pipe(
          map(() => StrategyActions.disableInterviewNotificationSuccess({ showWidget: action.showWidget })),
          catchError(() => of(StrategyActions.disableInterviewNotificationFailed()))
        );
      })
    )
  );

  // TODO: it's orphaned effect. Let's remove
  disableIntreviewSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StrategyActions.disableInterviewNotificationSuccess),
      map(() => {
        // TODO: fetchCustomer() - Let's align why do we have this here!
        return AppRootActions.fetchCustomer();
      })
    )
  );

  updateToAlmostProspect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StrategyActions.updateToAlmostProspect),
      concatLatestFrom(() => [this.store.pipe(select(fromAppRoot.selectCurrentInvestAccount))]),
      mergeMap(([, currentAccountDetail]) =>
        this.strategyService.updateToAlmostProspect().pipe(
          mergeMap(() => [
            AppRootActions.fetchCustomerAccountDetailSuccess({
              // we know that it's always step_status 2 after call this endpoint
              customerAccountDetail: {
                ...currentAccountDetail,
                step_status: 2,
                customer_journey: { ...currentAccountDetail.customer_journey, ql_flow_completed: true }
              }
            }),
            StrategyActions.updateToAlmostProspectSuccess()
          ]),
          catchError(() => of(StrategyActions.updateToAlmostProspectFail()))
        )
      )
    )
  );

  skipSaxoOnboardingt$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StrategyActions.skipSaxoOnboarding),
      mergeMap(() =>
        this.strategyService.skipSaxoOnboarding().pipe(
          map(() => AppRootActions.fetchCustomer()),
          catchError(() => of(StrategyActions.skipSaxoOnboardingFail()))
        )
      )
    )
  );

  updateToAlmostProspectSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StrategyActions.updateToAlmostProspectSuccess),
      map(() => StrategyActions.setTabIndex({ tabIndex: 5 }))
    )
  );

  updateQLOnboardingStep$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StrategyActions.updateQLOnboardingStep),
      mergeMap(({ step }) => {
        return this.strategyService.updateQLOnboardingStep(step).pipe(
          map((response: { data: { attributes: { ql_onboarding_step } } }) => {
            const step = response.data === null ? null : response.data.attributes.ql_onboarding_step;
            return StrategyActions.updateQLOnboardingStepSuccess({ step });
          }),
          catchError(() => of(StrategyActions.updateQLOnboardingStepFail()))
        );
      })
    )
  );

  showToastGeneralError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          ...[
            StrategyActions.updateStrategyEditModeFail,
            StrategyActions.discardStrategyChangesFail,
            StrategyActions.saveStrategyChangesFail,
            StrategyActions.sendCompanySuggestionFail,
            StrategyActions.updatePortfolioCompanyListFail,
            StrategyActions.updateFailed,
            StrategyActions.disableInterviewNotificationFailed,
            StrategyActions.updateToAlmostProspectFail,
            StrategyActions.undoStrategyActionsFail,
            StrategyActions.skipSaxoOnboardingFail,
            StrategyActions.saveStrategyChangesFailEve2,
            StrategyActions.updateRiskFailedEve2,
            StrategyActions.updateRiskRecheckFailedEve2
          ]
        ),
        tap(() => {
          this.toastNotificationService.show('critical', this.translateService.instant('personal.flash.error_rev') as string);
        })
      ),
    { dispatch: false }
  );

  constructor(
    private accountService: AccountService,
    private actions$: Actions,
    private toastNotificationService: ToastNotificationService,
    private translateService: TranslateService,
    private store: Store,
    private router: Router,
    private strategyService: StrategyService,
    private reportingService: ReportingService,
    private accountOpeningService: AccountOpeningService
  ) {}
}
